import { inject, Injectable, linkedSignal } from '@angular/core';
import {
  ROLE_ADMINISTRATOR,
  ROLE_FIELD_ADMIN,
  ROLE_FINANCE_ADMIN,
  ROLE_PHARMACY,
  ROLE_PHARMACY_OWNER,
  ROLE_PROGRAM_ADMIN,
  ROLE_PROGRAM_ADMIN_READ,
  ROLE_RECON_PARTNERS,
  ROLE_RETAIL_PHARMACY,
  ROLE_TOKEN_PHARMACY,
} from '@libs/shared/domain/master-data/user/shared-master-data-user-role.type';
import { AuthService } from '@services/auth.service';
import { AuthStorage } from '@services/auth.storage';
import { TenantService } from '@services/tenant.service';
import { UserApiService, UserUpdateModel } from '@services/user.api.service';
import { AgreementType } from '@shared/models/agreement-type.enum';
import { Tenant } from '@shared/models/tenant.model';
import { UserModel } from '@shared/models/user';
import { UserActionType } from '@shared/models/user-action.type';
import { UserRoleModel } from '@shared/models/user-role';
import { UserRole } from '@shared/models/user-role.model';
import { UserStatusType } from '@shared/models/user-status.type';
import { IUser } from '@shared/models/user.model';
import { set } from 'lodash';
import { of, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

interface StatusActionTextColor {
  action: UserActionType;
  text: string;
  color: 'danger' | 'success';
}

type StatusActionMappingType = Record<string, StatusActionTextColor>;

export const ROLES_WITH_ALL_TENANT_ACCESS = [
  ROLE_ADMINISTRATOR,
  ROLE_FINANCE_ADMIN,
  ROLE_PHARMACY_OWNER,
  ROLE_RECON_PARTNERS,
  ROLE_TOKEN_PHARMACY,
];
export const ROLES_WITH_ALL_PHARMACIES_ACCESS = [ROLE_ADMINISTRATOR, ROLE_FINANCE_ADMIN, ROLE_PROGRAM_ADMIN, ROLE_TOKEN_PHARMACY];

@Injectable({ providedIn: 'root' })
export class UserService {
  private apiService = inject(UserApiService);
  private authService = inject(AuthService);
  private tenantService = inject(TenantService);

  readonly userChanged = new Subject<void>();
  currentUser = linkedSignal(() => (this.authService.authStatus() ? UserService.getCurrent() : null));

  isUserAuthenticated() {
    return this.authService.isUserAuthenticated();
  }

  hasAllTenantsAccess(): boolean {
    return ROLES_WITH_ALL_TENANT_ACCESS.includes(this.currentUser()?.role ?? '');
  }

  hasAllPharmaciesAccess(): boolean {
    return ROLES_WITH_ALL_PHARMACIES_ACCESS.includes(this.currentUser()?.role ?? '');
  }

  hasAccessToTenant(tenantId: string): boolean {
    return this.hasAllTenantsAccess() || (this.currentUser()?.tenantIds || []).includes(tenantId);
  }

  static isCurrentUserSuperAdmin(): boolean {
    const roles = [ROLE_ADMINISTRATOR, ROLE_FINANCE_ADMIN];
    return roles.includes(this.getCurrentRole().rolename);
  }

  isSuperAdmin(): boolean {
    const roles = [ROLE_ADMINISTRATOR, ROLE_FINANCE_ADMIN];
    return roles.includes(this.currentUser()?.role ?? '');
  }

  /**
   * @deprecated
   * @see {@link UserService.getCurrent()}
   */
  getCurrentUser(): IUser {
    return this.authService.getCurrentUser();
  }

  getStatusByAction(action: UserActionType): UserStatusType {
    const statusActionMappings: Record<UserActionType, UserStatusType> = {
      reinvite: 'pending',
      enable: 'active',
      disable: 'disabled',
    };
    return statusActionMappings[action];
  }

  getUserAccessibleTenants(): Tenant[] {
    if (!this.isUserAuthenticated()) return [];
    const allTenants = this.tenantService.allTenants();
    const userTenants = UserService.getCurrent().tenantIds;
    return this.hasAllTenantsAccess() ? allTenants : allTenants.filter(tenant => userTenants.includes(tenant.id));
  }

  updateCurrentUser(userData: UserUpdateModel) {
    const user = Object.assign({}, this.currentUser(), userData);
    return this.apiService.update(userData).pipe(
      tap(() => {
        this.updateUserSession(user);
        this.currentUser.set(user);
        this.userChanged.next();
      }),
      catchError(err => {
        console.error('Error updating user:', err);
        return of(null);
      }),
    );
  }

  isInRole(role: string) {
    const user = this.currentUser();
    return user?.role ? user.role === role : false;
  }

  isAdministrator() {
    return this.isInRole(ROLE_ADMINISTRATOR);
  }

  isFinanceAdmin() {
    return this.isInRole(ROLE_FINANCE_ADMIN);
  }

  isFieldAdmin() {
    return this.isInRole(ROLE_FIELD_ADMIN);
  }

  isProgramAdmin() {
    return this.isInRole(ROLE_PROGRAM_ADMIN);
  }

  isPharmacy() {
    return this.isInRole(ROLE_PHARMACY);
  }

  isApiPharmacy() {
    return this.isInRole('ApiPharmacy');
  }

  isRetailPharmacy(): boolean {
    return this.isInRole(ROLE_RETAIL_PHARMACY);
  }

  isProgramAdminRead(): boolean {
    return this.isInRole(ROLE_PROGRAM_ADMIN_READ);
  }

  acceptAgreement(user: UserModel, agreement: AgreementType) {
    if (!this.hasAgreement(user, agreement)) {
      set(user, 'acceptedAgreements', user.acceptedAgreements ?? []);
      user.acceptedAgreements?.push({
        id: agreement,
        dateModified: new Date().toISOString(),
      });
    }
    return user;
  }

  hasAgreement(user: UserModel, agreement: AgreementType) {
    return !!(user.acceptedAgreements ?? []).find(item => item?.id === agreement);
  }

  hasAcceptedAgreements(user: UserModel): boolean {
    const roles: Record<string, AgreementType> = {
      [UserRole.PharmacyOwner]: AgreementType.USER_PHARMACY_OWNER,
    };
    const roleAgreement = roles[user.role];
    return roleAgreement ? this.hasAgreement(user, roles[user.role]) : true;
  }

  getUserTenant(): string | null {
    if (this.isSuperAdmin()) return null;
    const userTenants = this.currentUser()?.tenantIds;
    if (!userTenants || userTenants.length <= 0) return null;
    return userTenants[0];
  }

  private updateUserSession(user: UserModel) {
    localStorage.setItem(AuthStorage.USER, JSON.stringify(user));
    localStorage.setItem(AuthStorage.USER_OLD, JSON.stringify(UserService.mapUserToIUser(user)));
  }

  static readonly STATUS_ACTION_MAPPINGS: StatusActionMappingType = {
    active: { action: 'disable', text: 'Disable User', color: 'danger' },
    disabled: { action: 'enable', text: 'Activate User', color: 'success' },
    expired: { action: 'reinvite', text: 'Resend Invite', color: 'success' },
    pending: { action: 'reinvite', text: 'Resend Invite', color: 'success' },
  };

  static readonly USER_STATUS_CLASS: Record<string, string> = {
    active: 'success',
    disabled: 'info',
    expired: 'danger',
    pending: 'warning',
  };

  static isLoggedIn(): boolean {
    return !!localStorage.getItem(AuthStorage.USER);
  }

  static getCurrent(): UserModel {
    const item = localStorage.getItem(AuthStorage.USER);
    if (!item) {
      throw new Error('Getting user in non-user space!');
    }
    return JSON.parse(item);
  }

  static getCurrentRole(): UserRoleModel {
    const item = localStorage.getItem(AuthStorage.ROLE);
    if (!item) {
      throw new Error('Getting user role in non-user space!');
    }
    return JSON.parse(item);
  }

  static mapIUserToUser(user: IUser): UserModel {
    return {
      username: user.username,
      firstName: user.first_name,
      lastName: user.last_name,
      avatar: user.avatar,
      email: user.email,
      phone: user.phone,
      role: user.role,
      tenantIds: user.tenants ?? [],
      ncpdps: user.ncpdps ?? [],
      chainNpis: user.chain_npis ?? [],
      regions: user.business_regions ?? {},
      verifiedAt: user.date_verified,
      status: user.status,
      statusChangedAt: user.time_of_status,
      mfaEnabled: user.mfa_enabled,
      mfaType: user.mfa_type ?? 'email',
      acceptedAgreements: user.accepted_agreements,
    };
  }

  static mapUserToIUser(user: UserModel): IUser {
    return {
      username: user.username,
      first_name: user.firstName ?? '',
      last_name: user.lastName ?? '',
      avatar: user.avatar,
      email: user.email,
      phone: user.phone,
      role: user.role,
      tenants: user.tenantIds,
      ncpdps: user.ncpdps,
      chain_npis: user.chainNpis,
      business_regions: user.regions,
      date_verified: user.verifiedAt,
      status: user.status,
      time_of_status: user.statusChangedAt,
      mfa_enabled: user.mfaEnabled,
      mfa_type: user.mfaType,
      accepted_agreements: user.acceptedAgreements,
    };
  }
}
